import React from 'react';
import { CheckCircle2, Loader2, XCircle, Link2, AlertTriangle } from 'lucide-react';

const money = (v) => (v == null ? '—' : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

/** Statement set · per-file parse status + join timeline (reads `job.batch` from /upload-job-status). */
export const StatementSetProgress = ({ batch }) => {
  if (!batch?.files?.length) return null;
  const cont = batch.continuity;
  const joins = cont?.joins || [];
  const skipped = cont?.skipped || [];
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 mb-3 text-xs" data-testid="statement-set-progress">
      <div className="font-medium text-slate-800 mb-2">Statement set · {batch.files.length} files</div>
      <ul className="space-y-1" data-testid="statement-set-files">
        {batch.files.map((f) => {
          const parsed = f.status === 'parsed_for_batch';
          const failed = f.status === 'failed';
          const seg = cont?.segments?.find((s) => s.child_id === f.child_id);
          return (
            <li key={f.child_id} className="flex items-center gap-2 text-slate-700" data-testid={`statement-set-file-${f.child_id}`}>
              {failed ? <XCircle className="w-3.5 h-3.5 text-red-600" /> : parsed ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-600" />}
              <span className="truncate flex-1" title={f.filename}>{f.filename}</span>
              {seg ? (
                <span className="text-slate-500">{seg.period_start} → {seg.period_end} · {seg.rows} rows · {money(seg.statement_opening)} → {money(seg.statement_closing)}</span>
              ) : parsed ? <span className="text-slate-500">{f.rows} rows</span> : <span className="text-slate-400">reading…</span>}
            </li>
          );
        })}
      </ul>
      {joins.length > 1 && (
        <div className="mt-2 border-t border-slate-100 pt-2" data-testid="statement-set-joins">
          <div className="text-slate-600 mb-1 flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> Joins (closing balance must equal the next opening)</div>
          {joins.slice(1).map((j, i) => (
            <div key={i} className="flex items-center gap-2 text-slate-700" data-testid={`statement-set-join-${i}`}>
              {j.ok && j.printed_match !== false ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}
              <span className="truncate">{j.from} → {j.to}</span>
              <span className="text-slate-500">{money(j.books_balance ?? j.printed_close_prev)} = {money(j.statement_balance ?? j.printed_open_next)}{j.rows_trimmed ? ` · ${j.rows_trimmed} overlapping row(s) skipped` : ''}</span>
            </div>
          ))}
        </div>
      )}
      {cont?.blocking && (
        <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-red-800" data-testid="statement-set-blocking">
          <strong>{cont.blocking.previous_file || 'start'} → {cont.blocking.file}</strong>: {cont.message}
        </div>
      )}
      {skipped.length > 0 && (
        <div className="mt-2 text-slate-500" data-testid="statement-set-skipped">Skipped as duplicates: {skipped.map((s) => s.filename).join(', ')}</div>
      )}
    </div>
  );
};
